import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Bot, Send, Loader2, User as UserIcon, Wrench, AlertTriangle, CalendarClock,
} from 'lucide-react';
import { aiAssistantService } from '@/services/aiAssistantService';
import { ConversationSchedulesRow } from './ConversationSchedulesRow';

interface ChatMsg {
  role: 'user' | 'assistant' | 'error';
  text: string;
  tools?: string[];
  /** If this turn is from a scheduled run, the firing id — rendered as a
   *  small "Scheduled run · HH:mm" badge above the bubble. */
  scheduledRunId?: string | null;
  createdAt?: string;
}

// Compact markdown renderer for assistant replies — see the original Phase
// 2 design note; explicit element classes, no Tailwind `prose` (the
// typography plugin isn't wired into this v4 build).
const md = {
  h1: ({ node, ...p }: any) => <div className="font-semibold text-[0.95em] mt-2 mb-1" {...p} />,
  h2: ({ node, ...p }: any) => <div className="font-semibold text-[0.95em] mt-2 mb-1" {...p} />,
  h3: ({ node, ...p }: any) => <div className="font-semibold text-[0.92em] mt-2 mb-1" {...p} />,
  p: ({ node, ...p }: any) => <p className="my-1 leading-snug" {...p} />,
  ul: ({ node, ...p }: any) => <ul className="list-disc pl-4 my-1 space-y-0.5" {...p} />,
  ol: ({ node, ...p }: any) => <ol className="list-decimal pl-4 my-1 space-y-0.5" {...p} />,
  li: ({ node, ...p }: any) => <li className="leading-snug" {...p} />,
  strong: ({ node, ...p }: any) => <strong className="font-semibold" {...p} />,
  em: ({ node, ...p }: any) => <em className="italic" {...p} />,
  a: ({ node, ...p }: any) => (
    <a className="underline" target="_blank" rel="noopener noreferrer" {...p} />
  ),
  hr: ({ node, ...p }: any) => <hr className="my-2 border-border" {...p} />,
  blockquote: ({ node, ...p }: any) => (
    <blockquote className="border-l-2 border-border pl-2 italic my-1" {...p} />
  ),
  code: ({ node, ...p }: any) => (
    <code className="font-mono text-[0.85em] rounded bg-black/25 px-1 py-0.5" {...p} />
  ),
  pre: ({ node, ...p }: any) => (
    <pre
      className="my-1 overflow-x-auto rounded bg-black/25 p-2 text-[0.85em]
                 [&_code]:bg-transparent [&_code]:p-0"
      {...p}
    />
  ),
  table: ({ node, ...p }: any) => (
    <div className="overflow-x-auto my-1">
      <table className="w-full text-[0.85em] border-collapse" {...p} />
    </div>
  ),
  th: ({ node, ...p }: any) => (
    <th className="border border-border px-2 py-1 text-left font-semibold" {...p} />
  ),
  td: ({ node, ...p }: any) => <td className="border border-border px-2 py-1" {...p} />,
};

const MarkdownMessage: React.FC<{ text: string }> = ({ text }) => (
  <ReactMarkdown remarkPlugins={[remarkGfm]} components={md}>
    {text}
  </ReactMarkdown>
);

/**
 * Chat panel — single-side message stream (all bubbles left-aligned, role
 * differentiated by icon + colour, not by side). Loads an existing
 * conversation when a `conversationId` is supplied; on first send for a new
 * conversation it adopts the id returned by the `done` event and bubbles it
 * to the parent so the history rail can refresh.
 */
interface Props {
  context?: string;
  /** When set, the panel loads and continues this conversation. `null` means
   *  start fresh on first send. */
  conversationId?: string | null;
  /** Notify the parent the moment we have a real id (either loaded from the
   *  selection or returned by the first turn's `done`). */
  onConversationChanged?: (id: string | null) => void;
  /** Bumped after a turn finishes so the schedules row + history rail can
   *  refetch (a turn may have created or disabled a schedule). */
  onTurnComplete?: () => void;
}

export const AiChatPanel: React.FC<Props> = ({
  context = 'general',
  conversationId: selectedId = null,
  onConversationChanged,
  onTurnComplete,
}) => {
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [activity, setActivity] = useState<string | null>(null);
  // The "current" id — starts as the selection prop, gets updated to a real
  // id once the first turn lands a `done` with one.
  const [conversationId, setConversationId] = useState<string | null>(selectedId);
  const [loadingHistory, setLoadingHistory] = useState(false);
  // Bumped after every turn so the schedules row above the chat refetches —
  // a turn that called create_loop / disable_schedule has just changed the
  // schedule set for this conversation.
  const [schedulesRefreshKey, setSchedulesRefreshKey] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy, activity]);

  // Whenever the parent's selection changes, reload — either the existing
  // transcript or reset to empty for a fresh conversation.
  useEffect(() => {
    setConversationId(selectedId);
    if (selectedId === null) {
      setMessages([]);
      // Also clear the loading flag — a previous in-flight transcript fetch's
      // `finally` is gated on `!cancelled`, so without this the input stays
      // disabled when the user clicks "New conversation" mid-load.
      setLoadingHistory(false);
      return;
    }
    let cancelled = false;
    setLoadingHistory(true);
    aiAssistantService
      .getConversation(selectedId)
      .then((c) => {
        if (cancelled) return;
        setMessages(
          c.turns.map((t) => ({
            role: t.role.toLowerCase() === 'user' ? 'user' : 'assistant',
            text: t.text,
            scheduledRunId: t.scheduledRunId,
            createdAt: t.createdAt,
          })),
        );
      })
      .catch((e) => {
        if (cancelled) return;
        setMessages([{ role: 'error', text: e instanceof Error ? e.message : 'Failed to load conversation.' }]);
      })
      .finally(() => {
        if (!cancelled) setLoadingHistory(false);
      });
    return () => { cancelled = true; };
  }, [selectedId]);

  const updateConversationId = useCallback((id: string | null) => {
    setConversationId(id);
    onConversationChanged?.(id);
  }, [onConversationChanged]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text }]);
    setBusy(true);
    setActivity('Thinking…');
    const toolsUsed: string[] = [];
    let conversationStarted = false;
    try {
      await aiAssistantService.chatStream(
        { conversationId, message: text, context },
        (ev) => {
          if (ev.kind === 'tool') {
            if (ev.toolStatus === 'started' && ev.tool) {
              toolsUsed.push(ev.tool);
              setActivity(`Running ${ev.tool}…`);
            } else if (ev.toolStatus === 'finished') {
              setActivity('Thinking…');
            }
          } else if (ev.kind === 'message') {
            setMessages((m) => [...m, {
              role: 'assistant', text: ev.text || '(no response)', tools: toolsUsed.slice(),
            }]);
          } else if (ev.kind === 'done') {
            if (ev.conversationId && ev.conversationId !== conversationId) {
              updateConversationId(ev.conversationId);
              conversationStarted = true;
            }
          } else if (ev.kind === 'error') {
            if (ev.conversationId) updateConversationId(ev.conversationId);
            setMessages((m) => [...m, {
              role: 'error', text: ev.text ?? 'The assistant is unavailable.',
            }]);
          }
        },
      );
    } catch (e: any) {
      setMessages((m) => [...m, { role: 'error', text: e?.message ?? 'Request failed.' }]);
    } finally {
      setBusy(false);
      setActivity(null);
      // Refetch the conversation's schedules row — the turn may have called
      // create_loop / disable_schedule / create_schedule.
      setSchedulesRefreshKey((k) => k + 1);
      // Tell the parent a turn finished so the schedules row + history rail
      // can refresh (the turn may have created/disabled a schedule, and a
      // fresh conversation now exists in the rail).
      onTurnComplete?.();
      if (conversationStarted) onTurnComplete?.();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  return (
    <Card className="flex flex-col h-full overflow-hidden">
      {/* Schedules attached to this conversation, if any. Renders nothing
          when empty so a brand-new conversation isn't cluttered. */}
      {conversationId && (
        <div className="p-2 border-b">
          <ConversationSchedulesRow
            conversationId={conversationId}
            refreshKey={schedulesRefreshKey} />
        </div>
      )}

      {/* Messages — all rows left-aligned, role differentiated by icon
          (avatar) + bubble background, not by side. */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3">
        {loadingHistory && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading conversation…
          </div>
        )}
        {!loadingHistory && messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground gap-2">
            <Bot className="h-8 w-8 text-primary/60" />
            <div className="text-sm font-medium">AI trading assistant</div>
            <p className="text-xs max-w-xs">
              Ask about your strategies, runs, account or positions — the assistant
              fetches your real data to answer. Tell it to "run this every 30
              minutes" and it sets up a loop on this conversation.
            </p>
          </div>
        )}
        {messages.map((m, i) => {
          const isUser = m.role === 'user';
          const isError = m.role === 'error';
          return (
            <div key={i} className="flex flex-col gap-1">
              {m.scheduledRunId && (
                <div className="flex items-center gap-1 text-[11px] text-muted-foreground pl-9">
                  <CalendarClock className="h-3 w-3" />
                  <span>Scheduled run</span>
                  {m.createdAt && <span>· {formatTime(m.createdAt)}</span>}
                </div>
              )}
              <div className="flex gap-2">
                <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 ${
                  isUser ? 'bg-primary/15'
                  : isError ? 'bg-destructive/15' : 'bg-emerald-500/15'
                }`}>
                  {isUser ? <UserIcon className="h-3.5 w-3.5" />
                   : isError ? <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                   : <Bot className="h-3.5 w-3.5 text-emerald-500" />}
                </div>
                <div className={`flex-1 min-w-0 rounded-lg px-3 py-2 text-sm break-words ${
                  isUser ? 'bg-primary/10 whitespace-pre-wrap'
                  : isError ? 'bg-destructive/10 text-destructive whitespace-pre-wrap'
                  : 'bg-muted'
                }`}>
                  {m.role === 'assistant' ? <MarkdownMessage text={m.text} /> : m.text}
                  {m.tools && m.tools.length > 0 && (
                    <div className="mt-1.5 flex items-center gap-1 text-[11px] text-muted-foreground">
                      <Wrench className="h-3 w-3" />
                      {Array.from(new Set(m.tools)).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {busy && (
          <div className="flex gap-2">
            <div className="h-7 w-7 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0">
              <Bot className="h-3.5 w-3.5 text-emerald-500" />
            </div>
            <div className="rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> {activity ?? 'Thinking…'}
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t p-2 flex items-end gap-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          rows={2}
          placeholder="Ask the assistant…  (Enter to send, Shift+Enter for newline)"
          className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm
                     focus:outline-none focus:ring-2 focus:ring-ring"
          disabled={busy || loadingHistory}
        />
        <Button size="sm" onClick={() => void send()} disabled={busy || loadingHistory || !input.trim()}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </Card>
  );
};

function formatTime(iso: string): string {
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

export default AiChatPanel;
